import { motion, useMotionValue, useTransform, animate, type AnimationPlaybackControls } from 'framer-motion'
import { useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

export const GRADIENT = 'linear-gradient(135deg, #FF9D76 0%, #C176FF 50%, #76F1FF 100%)'

// All 4 brand colors — mirror repeat handles the seamless reverse loop
const GRADIENT_LOOP = 'linear-gradient(135deg, #FF9D76 0%, #C176FF 33%, #76F1FF 67%, #FFE176 100%)'

/**
 * Spread these variants onto any motion child of GradientHoverCard to sync
 * its opacity with the card border (faint at rest, vivid on hover).
 *
 * @example
 * <motion.div variants={gradientBorderVariants} transition={{ duration: 0.3, ease: 'easeOut' }} />
 */
export const gradientBorderVariants = {
  idle:    { opacity: 0.35 },
  hovered: { opacity: 1 },
} as const

interface GradientHoverCardProps {
  children: React.ReactNode
  className?: string
}

export function GradientHoverCard({ children, className }: GradientHoverCardProps) {
  const bgPos = useMotionValue(0)
  const backgroundPosition = useTransform(bgPos, v => `${v}% ${v}%`)
  const loopRef = useRef<AnimationPlaybackControls | null>(null)

  const handleHoverStart = () => {
    loopRef.current?.stop()
    // Mirror repeat: 0→100→0→… so the gradient oscillates within brand colors only
    loopRef.current = animate(bgPos, [0, 100], {
      duration: 8,
      repeat: Infinity,
      repeatType: 'mirror',
      ease: 'easeInOut',
    })
  }

  const handleHoverEnd = () => {
    loopRef.current?.stop()
    loopRef.current = null
  }

  useEffect(() => () => { loopRef.current?.stop() }, [])

  return (
    <motion.div
      className={cn('relative isolate rounded-xl [will-change:transform] [backface-visibility:hidden]', className)}
      initial="idle"
      whileHover="hovered"
      variants={{
        idle: {
          y: 0,
          scale: 1,
          zIndex: 0,
          boxShadow: '0 0px 0px 0px rgba(0,0,0,0), 0 0px 0px 0px rgba(193,118,255,0)',
        },
        hovered: {
          y: -4,
          scale: 1.03,
          zIndex: 10,
          boxShadow: '0 20px 40px -8px rgba(0,0,0,0.2), 0 0 25px -5px rgba(193,118,255,0.3)',
        },
      }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      onHoverStart={handleHoverStart}
      onHoverEnd={handleHoverEnd}
    >
      {/* Gradient border: faint at rest, vivid and colour-cycling on hover */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-xl"
        style={{ background: GRADIENT_LOOP, backgroundSize: '300% 300%', backgroundPosition, zIndex: -1 }}
        variants={{ idle: { opacity: 0.35 }, hovered: { opacity: 1 } }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      />
      {/* Inner background — masks centre, leaving the 2 px border ring */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-[2px] rounded-[10px] bg-background"
        style={{ zIndex: -1 }}
      />
      {/* Gradient fill bloom */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-[2px] rounded-[10px]"
        style={{ background: GRADIENT_LOOP, backgroundSize: '300% 300%', backgroundPosition, filter: 'blur(10px)', zIndex: -1 }}
        variants={{ idle: { opacity: 0 }, hovered: { opacity: 0.18 } }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      />

      {children}
    </motion.div>
  )
}
